import React, { useState, useEffect } from 'react';
import { TextField, Radio, AppBar, Toolbar, Typography, Paper, Container, RadioGroup, Box, Modal, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Grid, Checkbox, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Chip } from '@mui/material';
import { useNavigate } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ─────────────────────────────────────────────
// Info data for the dialog
// ─────────────────────────────────────────────
const addChargeInfoSections = [
    {
        title: "Charge Name",
        color: "#1976D2",
        description: "Enter the name of the new charge. This will be the label displayed across fee-related screens.",
        fields: ["Text input field", "Required to identify the charge"],
    },
    {
        title: "Charge Type",
        color: "#7B1FA2",
        description: "Select whether this charge is mandatory for all students or optional.",
        fields: ["Required – Charge is mandatory for all applicable students", "Optional – Charge is not mandatory"],
    },
    {
        title: "Student Applicability",
        color: "#388E3C",
        description: "Define which category of students this charge applies to. Both options can be selected simultaneously.",
        fields: ["New Student – Applies to newly admitted students", "Old Student – Applies to existing/returning students"],
    },
    {
        title: "Charge Applicable On",
        color: "#F57C00",
        description: "Select whether this charge is applied at the time of admission or on a monthly recurring basis.",
        fields: ["Admission – One-time charge at the time of admission", "Monthly – Recurring charge applied every selected month"],
    },
    {
        title: "Month Selection",
        color: "#0277BD",
        description: "If the charge is Monthly, specify which months it applies to. You can select all or choose individual months.",
        fields: [
            "All Months – Automatically selects and locks all months",
            "Selected Months – Allows manual selection of individual months from the table",
            "Month checkboxes are disabled when 'All Months' is active",
        ],
    },
    {
        title: "Actions",
        color: "#C62828",
        description: "Use the action buttons to submit the new charge or reset all fields to their default values.",
        fields: [
            "SAVE – Submits the new charge via POST request and resets the form on success",
            "RESET – Clears all fields and resets the form to default values without saving",
        ],
    },
];

function AddChargeMaster() {
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    // ── Info dialog state ──
    const [infoOpen, setInfoOpen] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    let navigate = useNavigate();
    const [chargeName, setChargeName] = useState('');
    const [chargeType, setChargeType] = useState('required');
    const [isNewStudent, setIsNewStudent] = useState(false);
    const [isOldStudent, setIsOldStudent] = useState(false);
    const [admissionType, setAdmissionType] = useState('admission');
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [months, setMonths] = useState([]);
    const [monthSelectionType, setMonthSelectionType] = useState('selected-month');
    const [cancelClickCount, setCancelClickCount] = useState(0);

    useEffect(() => {
        const fetchMonths = async () => {
            try {
                const apiUrl = process.env.REACT_APP_BASE_URL;
                const response = await fetch(`${apiUrl}/Fine/GetMonthList`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: sessionStorage.getItem('token'),
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setMonths(data);
                } else {
                    // alert('Failed to fetch months');
                    setSnackbarMessage('Failed to fetch months');
                    setOpenSnackbar(true);
                    setSnackbarSeverity("error")
                }
            } catch (error) {
                console.error('API request error:', error);
                // alert('An error occurred while fetching months.');
                setSnackbarMessage('An error occurred while fetching months.');
                setOpenSnackbar(true);
                setSnackbarSeverity("error")
            }
        };
        fetchMonths();
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        const payload = {
            chargeName,
            chargeType: admissionType,
            required: chargeType === 'required' ? 1 : 0,
            newStudent: isNewStudent ? 1 : 0,
            oldStudent: isOldStudent ? 1 : 0,
            monthData: months.map(month => ({
                isChecked: selectedMonths.includes(month.month),
                monthId: String(month.monthId) // Ensure monthId is a string
            }))
        };
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const response = await fetch(
                `${apiUrl}/ChargeMaster`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: sessionStorage.getItem('token'),
                    },
                    body: JSON.stringify(payload),
                }
            );
            if (response.ok) {
                setShowSuccessModal(true);
                setChargeName('');
                setChargeType('required');
                setIsNewStudent(false);
                setIsOldStudent(false);
                setAdmissionType('admission');
                setSelectedMonths([]);
                setMonthSelectionType('selected-month');

            } else {
                // alert('Unable to add a charge master');
                setSnackbarMessage('Unable to add a charge master');
                setOpenSnackbar(true);
                setSnackbarSeverity("error")
            }
        } catch (error) {
            console.error('API request error:', error);
            // alert('An error occurred. Please try again later.');
            setSnackbarMessage('An error occurred. Please try again later.');
            setOpenSnackbar(true);
            setSnackbarSeverity("error")
        }
    };

    const handleCancel = () => {
        setChargeName('');
        setChargeType('required');
        setIsNewStudent(false);
        setIsOldStudent(false);
        setAdmissionType('admission');
        setSelectedMonths([]);
        setMonthSelectionType('selected-month');
    };

    const handleAllMonthsSelect = () => {
        setSelectedMonths(months.map(month => month.month));
    };

    const handleMonthSelectionChange = (event) => {
        const value = event.target.value;
        setMonthSelectionType(value);
        if (value === 'all-months') {
            handleAllMonthsSelect();
        } else {
            setSelectedMonths([]);
        }
    };

    const handleMonthCheckboxChange = (monthName) => {
        setSelectedMonths(prev => {
            if (prev.includes(monthName)) {
                return prev.filter(item => item !== monthName);
            } else {
                return [...prev, monthName];
            }
        });
    };

    return (
        <Container>
            <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
                <Toolbar>
                    <Typography variant="h4" component="div" style={{ fontWeight: 'bold', flexGrow: 1 }}>
                        Add Charge Details
                    </Typography>

                    {/* ── Info Button ── */}
                    <IconButton
                        color="inherit"
                        onClick={() => setInfoOpen(true)}
                        title="Form Information"
                        style={{
                            backgroundColor: "rgba(255,255,255,0.15)",
                            border: "2px solid rgba(255,255,255,0.6)",
                            borderRadius: "50%",
                            padding: "6px",
                        }}
                    >
                        <InfoIcon style={{ fontSize: 26 }} />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* ── Info Dialog ── */}
            <Dialog
                open={infoOpen}
                onClose={() => setInfoOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ style: { borderRadius: 16, padding: 0 } }}
            >
                <DialogTitle
                    style={{
                        backgroundColor: "#0B1F3D",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "16px 24px",
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <InfoIcon style={{ marginRight: 8 }} />
                        <Typography variant="h6" style={{ fontWeight: "bold" }}>
                            Add Charge Details — Form Guide
                        </Typography>
                    </Box>
                    
                </DialogTitle>

                <DialogContent dividers style={{ backgroundColor: "#f9f9f9", padding: "24px" }}>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
                        This form is used to create a new charge record. Fill in all relevant details and click SAVE to submit. Use RESET to clear the form at any time.
                    </Typography>

                    {addChargeInfoSections.map((section, index) => (
                        <Box
                            key={index}
                            style={{
                                backgroundColor: "#fff",
                                borderRadius: 12,
                                padding: "18px 20px",
                                marginBottom: 16,
                                borderLeft: `5px solid ${section.color}`,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                            }}
                        >
                            <Typography
                                variant="h6"
                                style={{ fontWeight: "bold", color: section.color, marginBottom: 6 }}
                            >
                                {section.title}
                            </Typography>

                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                                {section.description}
                            </Typography>

                            <Divider style={{ marginBottom: 10 }} />

                            <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 6 }}>
                                📝 DETAILS
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                                {section.fields.map((field, i) => (
                                    <Chip
                                        key={i}
                                        label={field}
                                        size="small"
                                        variant="outlined"
                                        style={{
                                            borderColor: section.color,
                                            color: section.color,
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ))}
                </DialogContent>

                <DialogActions style={{ padding: "12px 24px", backgroundColor: "#f9f9f9" }}>
                    <Button
                        onClick={() => setInfoOpen(false)}
                        variant="contained"
                        style={{ backgroundColor: "#0B1F3D", color: "#fff", borderRadius: 20, paddingInline: 28 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid container spacing={2} style={{ marginTop: 3 }}>
                <Grid item xs={6}>
                    <Paper elevation={3} style={{ padding: 16 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Charge Name"
                                    value={chargeName}
                                    onChange={(e) => setChargeName(e.target.value)}
                                    fullWidth
                                    placeholder="Enter Charge Name"
                                    margin="normal"
                                />
                            </Grid>
                            {/* <Grid item xs={12}>
                                <Typography variant="subtitle1">Charge Type</Typography>
                                <RadioGroup
                                    aria-label="charge-type"
                                    name="charge-type"
                                    value={chargeType}
                                    onChange={(e) => setChargeType(e.target.value)}
                                    row
                                >
                                    <FormControlLabel value="required" control={<Radio />} label="Required" />
                                    <FormControlLabel value="optional" control={<Radio />} label="Optional" />
                                </RadioGroup>
                            </Grid> */}
                            <Grid item xs={6}>
                                <FormControlLabel
                                    control={<Checkbox checked={isNewStudent} onChange={(e) => setIsNewStudent(e.target.checked)} />}
                                    label="New Student"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                {/* <FormControlLabel
                                    control={<Checkbox checked={isOldStudent} onChange={(e) => setIsOldStudent(e.target.checked)} />}
                                    label="Old Student"
                                /> */}
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">Charge Applicable on</Typography>
                                <RadioGroup
                                    aria-label="admission-type"
                                    name="admission-type"
                                    value={admissionType}
                                    onChange={(e) => setAdmissionType(e.target.value)}
                                    row
                                >
                                    {/* <FormControlLabel value="Admission" control={<Radio />} label="Admission" /> */}
                                    <FormControlLabel value="Monthly" control={<Radio />} label="Monthly" />
                                </RadioGroup>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">Month Selection</Typography>
                                <RadioGroup
                                    aria-label="month-selection"
                                    name="month-selection"
                                    value={monthSelectionType}
                                    onChange={handleMonthSelectionChange}
                                    row
                                >
                                    <FormControlLabel value="all-months" control={<Radio />} label="All Months" />
                                    <FormControlLabel value="selected-month" control={<Radio />} label="Selected Months" />
                                </RadioGroup>
                            </Grid>
                            <Grid item xs={12} style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                <Button variant="contained" color="primary" onClick={handleSave}>SAVE</Button>
                                <Button variant="contained" color="error" onClick={handleCancel}>RESET</Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item xs={6}>
                    <Paper elevation={3} style={{ padding: 16, height: '100%', overflowY: 'auto' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Select</TableCell>
                                        <TableCell>Month</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {months.map((month, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={selectedMonths.includes(month.month)}
                                                    onChange={() => handleMonthCheckboxChange(month.month)}
                                                    disabled={monthSelectionType === 'all-months'}
                                                />
                                            </TableCell>
                                            <TableCell>{month.month}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            <Modal
                open={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                aria-labelledby="success-modal-title"
                aria-describedby="success-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
                        <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
                            Charge Added ✅
                        </Typography>
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: '20px' }}>
                        <Typography id="success-modal-description" sx={{ mb: 2 }}>
                            The Charge has been added successfully.
                        </Typography>

                        {/* Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    navigate(-1); // go back after closing modal
                                }}
                                variant="contained"
                                color="primary"
                            >
                                OK
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default AddChargeMaster;